import React from "react";
import { useLang } from "../lib/LangContext";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_a942c98c-21f3-4b68-8acb-90399d58ab6a/artifacts/mz3deqo2_Screenshot%202026-06-24%20at%2003.06.48.png";

const Hero = () => {
  const { t } = useLang();

  return (
    <section
      data-testid="hero-section"
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden bg-black grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Adtree Go cinematic brand visual"
          className="absolute inset-0 w-full h-full object-cover object-[70%_center] opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" />
      </div>

      {/* Vertical year marker */}
      <div className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 z-10">
        <div className="font-anton text-xs tracking-[0.6em] text-white/60 vertical-rl">20</div>
        <div className="w-px h-24 bg-white/30" />
        <div className="font-anton text-xs tracking-[0.6em] text-white/60 vertical-rl">26</div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 pt-28 md:pt-32 pb-16 md:pb-20 flex flex-col justify-center min-h-[100svh]">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-5 fade-up">
            <div className="h-px w-8 bg-brand-yellow" />
            <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-brand-yellow font-semibold">
              {t.hero.eyebrow}
            </span>
          </div>
          <h1
            data-testid="hero-title"
            className="font-anton uppercase text-white leading-[0.95] tracking-tight fade-up"
            style={{ animationDelay: "0.1s", fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}
          >
            {t.hero.title1}
            <br />
            <span className="text-brand-yellow text-glow">{t.hero.title2}</span>
          </h1>
          <p
            className="mt-6 md:mt-8 text-sm md:text-base lg:text-lg text-white/70 max-w-xl leading-relaxed fade-up"
            style={{ animationDelay: "0.25s" }}
          >
            {t.hero.desc}
          </p>

          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 fade-up" style={{ animationDelay: "0.35s" }}>
            <a
              href="#register"
              data-testid="hero-cta-join"
              className="group inline-flex items-center justify-center gap-3 bg-brand-yellow text-black font-anton uppercase tracking-widest text-xs sm:text-sm md:text-base px-6 py-3.5 md:px-7 md:py-4 hover:bg-white transition-all"
            >
              {t.cta.joinNetwork}
              <ArrowDownRight size={16} className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
            </a>
            <a
              href="#about"
              data-testid="hero-cta-explore"
              className="inline-flex items-center justify-center gap-3 border border-white/20 text-white font-anton uppercase tracking-widest text-xs sm:text-sm md:text-base px-6 py-3.5 md:px-7 md:py-4 hover:border-brand-yellow hover:text-brand-yellow transition-colors"
            >
              {t.cta.explore}
            </a>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-12 md:mt-20 grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 max-w-3xl fade-up" style={{ animationDelay: "0.5s" }}>
          {[
            { n: "4,500+", l: t.hero.stat1 },
            { n: "400+", l: t.hero.stat2 },
            { n: "500,000K+", l: t.hero.stat3 },
            { n: "10M+", l: t.hero.stat4 },
          ].map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`} className="border-l-2 border-brand-yellow pl-3 md:pl-4">
              <div className="font-anton text-2xl sm:text-2xl md:text-3xl lg:text-4xl text-white leading-none">{s.n}</div>
              <div className="text-[9px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.25em] text-white/60 mt-1.5 md:mt-2">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom checker strip */}
      <div className="absolute bottom-0 left-0 right-0 h-6 checker opacity-90 pointer-events-none z-10" />
    </section>
  );
};

export default Hero;
